/**
 * components/procurement/PerformanceSection.jsx — Contract Performance.
 *
 * Editable usage band (Expected / Min% / Max%) + a usage chart that overlays
 * Actual usage (from bills) against the Expected / Max / Min bands.
 */
import React, { useEffect, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, Legend,
} from 'recharts';
import { toast } from 'sonner';
import { Save, Activity } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { getPerformance, patchContract } from '../../api/contracts';

const compact = (n) => {
  if (n == null || isNaN(n)) return '0';
  const a = Math.abs(n);
  if (a >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return Math.round(n).toLocaleString();
};
const monthLabel = (m) => {
  if (!m || m.length < 7) return m;
  const [y, mo] = m.split('-');
  return `${['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][+mo]} '${y.slice(2)}`;
};

export function PerformanceSection({ accountId, contract, canEdit, onChanged }) {
  const perf = contract.performance || {};
  const [form, setForm] = useState({
    expected_usage: perf.expected_usage ?? '',
    min_usage_pct: perf.min_usage_pct ?? '',
    max_usage_pct: perf.max_usage_pct ?? '',
    uom: perf.uom || 'kWh',
  });
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const d = await getPerformance({ accountId, id: contract.id });
      setData(d);
    } catch (e) {
      toast.error('Failed to load performance', { description: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [contract.id]);

  const save = async () => {
    setSaving(true);
    try {
      await patchContract({ accountId, id: contract.id, patch: {
        performance: {
          expected_usage: form.expected_usage === '' ? null : Number(form.expected_usage),
          min_usage_pct: form.min_usage_pct === '' ? null : Number(form.min_usage_pct),
          max_usage_pct: form.max_usage_pct === '' ? null : Number(form.max_usage_pct),
          uom: form.uom,
        },
      } });
      toast.success('Performance settings saved');
      onChanged?.();
      load();
    } catch (e) {
      toast.error('Save failed', { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  const chartData = (data?.series || []).map((s) => ({
    month: monthLabel(s.month),
    Actual: s.actual_usage,
    Expected: s.expected_usage,
    Max: s.max_usage,
    Min: s.min_usage,
  }));
  const s = data?.summary;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm font-semibold"><Activity className="w-4 h-4" /> Contract Performance</div>
      <p className="text-xs text-muted-foreground -mt-2">Change the usage band to analyse the inputs against actual usage pulled from bills.</p>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end bg-muted/40 p-3 rounded-md">
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">Expected Usage ({form.uom})</Label>
          <Input type="number" value={form.expected_usage} disabled={!canEdit} onChange={(e) => setForm((f) => ({ ...f, expected_usage: e.target.value }))} data-testid="perf-expected-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">Max Usage (%)</Label>
          <Input type="number" value={form.max_usage_pct} disabled={!canEdit} onChange={(e) => setForm((f) => ({ ...f, max_usage_pct: e.target.value }))} placeholder="e.g. 110" data-testid="perf-max-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">Min Usage (%)</Label>
          <Input type="number" value={form.min_usage_pct} disabled={!canEdit} onChange={(e) => setForm((f) => ({ ...f, min_usage_pct: e.target.value }))} placeholder="e.g. 90" data-testid="perf-min-input" />
        </div>
        {canEdit && (
          <Button onClick={save} disabled={saving} data-testid="perf-save-btn"><Save className="w-4 h-4 mr-1.5" />{saving ? 'Saving…' : 'Save'}</Button>
        )}
      </div>

      {s && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="border rounded-md p-3"><div className="text-xs text-muted-foreground">Total Usage</div><div className="text-xl font-bold">{compact(s.total_usage)} <span className="text-xs font-normal">{form.uom}</span></div></div>
          <div className="border rounded-md p-3"><div className="text-xs text-muted-foreground">Total Cost</div><div className="text-xl font-bold">${compact(s.total_cost)}</div></div>
          <div className="border rounded-md p-3"><div className="text-xs text-muted-foreground">Avg Rate</div><div className="text-xl font-bold">{s.avg_rate != null ? `$${s.avg_rate}` : '—'}</div></div>
          <div className="border rounded-md p-3"><div className="text-xs text-muted-foreground">Band Breaches</div><div className="text-xl font-bold flex items-center gap-2">{s.breaches}{s.breaches > 0 && <Badge variant="destructive" className="text-[10px]">out of band</Badge>}</div></div>
        </div>
      )}

      <div className="border rounded-md p-4">
        <div className="text-sm font-semibold mb-3">Usage Chart</div>
        {loading ? (
          <div className="h-72 flex items-center justify-center text-sm text-muted-foreground">Loading…</div>
        ) : chartData.length === 0 ? (
          <div className="h-72 flex flex-col items-center justify-center text-sm text-muted-foreground" data-testid="perf-chart-empty">
            <Activity className="w-8 h-8 mb-2 opacity-40" />
            No actual usage found. Link contracted sites (properties / accounts) with matching commodity bills to populate this chart.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300} data-testid="perf-chart">
            <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={compact} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => (v == null ? '—' : Number(v).toLocaleString())} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="Actual" stroke="#dc2626" strokeWidth={2} dot={{ r: 2 }} />
              <Line type="monotone" dataKey="Expected" stroke="#1e3a8a" strokeDasharray="5 5" dot={false} />
              <Line type="monotone" dataKey="Max" stroke="#38bdf8" strokeDasharray="3 3" dot={false} />
              <Line type="monotone" dataKey="Min" stroke="#0ea5e9" strokeDasharray="3 3" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

export default PerformanceSection;
